import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';
import {
  Breadcrumbs,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { FilterAlt, Home as HomeIcon } from '@mui/icons-material';
import { RequestService2Icon } from '../../../common/Icons';
import PageTitle from '../../../common/PageTitle';
import LinkNavegacao from '../../../common/Link';
import { useAppContext } from '../../../../contexts/AppContext';
import { checkPermissionsAndRedirect, getFormErrorMessage } from '../../../../configs/functions';
import { listaEmpresas } from '../../management/companies/configs/functions';
import { useCommonItems } from '../../../../contexts/CommonItensProvider';
import { TooltipSelectDisabled } from '../../../common/TooltipSelectDisabled';
import { listaUsuariosFilter } from '../../management/people/configs/functions';
import { LoadingTableRow } from '../../../common/LoadingTableRow';
import { EmptyTableRow } from '../../../common/EmptyTableRow';
import { CustomTablePagination } from '../../../common/CustomTablePagination';
import { RetiradaExtraRow } from './components/RetiradaExtraRow';
import { ApproveRetiradaExtraModal } from './components/ApproveRetiradaExtraModal';
import { SelectEmpresaFilter } from '../../../common/SelectEmpresaFilter';
import { listaRetiradasExtra } from './functions';
import { PaginatedUserAutocomplete } from '../../../common/PaginatedUserAutocomplete';
import DateRangePicker from '../../../common/DateRangePicker';

export function ListOfRetiradasExtras() {
  const { exibirAlerta, exibirDialog, handleCloseDialog } = useCommonItems();
  const { getEmpresaIdSession, setEmpresaIdSession } = useAppContext();

  // estados para controle de paginacao
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [numTotalItems, setNumTotalItems] = useState(0);

  const initialDataInicio = dayjs().subtract(1, 'month');
  const initialDataFim = dayjs();

  const [dateRange, setDateRange] = useState([initialDataInicio, initialDataFim]);

  const [retiradasExtra, setRetiradasExtra] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [openForm, setOpenForm] = useState(false);

  const [pessoas, setPessoas] = useState([]);
  const [pagePessoas, setPagePessoas] = useState(0);
  const [isLoadingPessoas, setIsLoadingPessoas] = useState(false);
  const [hasMorePessoas, setHasMorePessoas] = useState(true);

  const [selectedEmpresaId, setSelectedEmpresaId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearched, setIsSearched] = useState(false);

  const {
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
    reset,
    control,
    formState: { errors },
  } = useForm();

  async function loadReportData(id_empresas, id_pessoa, statusRetirada, data_inicio, data_fim, id_responsavel, id_responsavel_aprovacao) {
    try {
      setIsLoading(true);

      data_inicio = dayjs(data_inicio).format('YYYY-MM-DD');
      data_fim = dayjs(data_fim).format('YYYY-MM-DD');

      const filter = {
        data_inicio,
        data_fim,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };

      if (id_empresas === 'all') {
        filter.empresa = empresas.map((empresa) => empresa.id);
      } else {
        filter.id_empresas = [id_empresas];
      }

      if (id_pessoa) filter.id_pessoa = id_pessoa;
      if (id_responsavel) filter.id_responsavel = id_responsavel;
      if (id_responsavel_aprovacao) filter.id_responsavel_aprovacao = id_responsavel_aprovacao;
      if (statusRetirada) filter.status = statusRetirada;

      const res = await listaRetiradasExtra(filter);
      setRetiradasExtra(res.data.data);
      setNumTotalItems(res.data.numero_total);
    } catch (error) {
      console.log(error);
      exibirAlerta('Ops', 'Ocorreu um erro ao carregar dados do relatório.', 'error');
    } finally {
      setIsLoading(false);
    }
  }

  const onSubmit = async (data) => {
    try {
      setIsSearched(true);
      setPage(0);
      loadReportData(data.id_empresas, data.id_pessoa, data.statusRetirada, data.data_inicio, data.data_fim, data.id_responsavel, data.id_responsavel_aprovacao);
    } catch (error) {
      exibirAlerta('Erro', 'Erro ao carregar dados do relatório', 'error');
      setIsSearched(false);
    }
  };

  const listaColaboradores = async (currentPage = 0, initialPageValue = false, filterValue = "") => {
    if (!selectedEmpresaId) return;

    if (initialPageValue) {
      setPessoas([]);
      setPagePessoas(0);
      setHasMorePessoas(true);
    }

    try {
      setIsLoadingPessoas(true);

      const filters = {
        id_empresa: selectedEmpresaId,
        limit: 10,
        offset: (initialPageValue ? 0 : currentPage) * 10,
      };

      // if nome is only number, then it's matricula
      if (filterValue && !isNaN(filterValue)) {
        filters.matricula = filterValue;
        delete filters.nome;
      } else {
        filters.nome = filterValue;
        delete filters.matricula;
      }

      filters.isFilter = true;
      const response = await listaUsuariosFilter(filters);
      const newOptions = response.data.data;

      setPessoas((prevOptions) => [...prevOptions, ...newOptions]);
      setHasMorePessoas(newOptions.length > 0);
      setPagePessoas((prevPage) => prevPage + 1);
    } catch (error) {
      console.error("Erro ao carregar colaboradores:", error);
    } finally {
      setIsLoadingPessoas(false);
    }
  }

  useEffect(() => {
    const requiredPermissionsView = ["admin", "admin_retiradaExtra", "list_retiradaExtra"];
    checkPermissionsAndRedirect(requiredPermissionsView);

    let defaultEmpresaId = null;
    const selectedEmpresaIdSession = getEmpresaIdSession();
    if (selectedEmpresaIdSession) {
      defaultEmpresaId = selectedEmpresaIdSession;
      setSelectedEmpresaId(selectedEmpresaIdSession);
    }

    listaEmpresas(null, null, null, true)
      .then((response => {
        exibirDialog('Carregando dados da Empresa...');
        const empresasData = response?.data.data;
        setEmpresas(empresasData);

        if (!selectedEmpresaIdSession) {
          defaultEmpresaId = empresasData[0].id;
          setSelectedEmpresaId(empresasData[0].id);
          setEmpresaIdSession(empresasData[0].id);
        }

        const defaultValues = {
          id_empresas: defaultEmpresaId,
          id_pessoa: '',
          id_responsavel: '',
          id_responsavel_aprovacao: '',
          statusRetirada: '',
          data_inicio: initialDataInicio,
          data_fim: initialDataFim
        };
        reset(defaultValues);

        loadReportData(defaultEmpresaId, null, null, defaultValues.data_inicio, defaultValues.data_fim, defaultValues.id_responsavel, defaultValues.id_responsavel_aprovacao);
      }))
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        handleCloseDialog();
      });

    return () => {
      reset();
    }
  }, []);

  useEffect(() => {
    listaColaboradores(0, true, "");
  }, [selectedEmpresaId]);

  useEffect(() => {
    if (!openForm) {
      const id_empresas = getValues('id_empresas');
      const id_pessoa = getValues('id_pessoa');
      const id_responsavel = getValues('id_responsavel');
      const id_responsavel_aprovacao = getValues('id_responsavel_aprovacao');
      const statusRetirada = getValues('statusRetirada');
      const data_inicio = getValues('data_inicio')
      const data_fim = getValues('data_fim')

      if (!id_empresas || !data_inicio || !data_fim) {
        return;
      }

      loadReportData(id_empresas, id_pessoa, statusRetirada, data_inicio, data_fim, id_responsavel, id_responsavel_aprovacao);
    }
  }, [page, rowsPerPage, openForm]);

  return (
    <>
      <Helmet title="Retiradas Extras" defer={false} />

      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="stretch"
        spacing={3}
      >
        <Grid item xs={12}>
          <Breadcrumbs>
            <LinkNavegacao to='/'><HomeIcon fontSize='small' /></LinkNavegacao>
            <LinkNavegacao to='/epis'>Gestão de EPI's</LinkNavegacao>
            <LinkNavegacao to='/epis/retiradas'>Retiradas</LinkNavegacao>
            <Typography variant='span'>Retiradas Extras</Typography>
          </Breadcrumbs>
          <PageTitle icon={<RequestService2Icon fontSize='large' />} title="Retiradas Extras" />
        </Grid>

        <Grid container item xs={12} spacing={3} component="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container item xs={12} md={12} spacing={3}>
            <Grid item xs={12} md={4}>
              <SelectEmpresaFilter
                empresas={empresas}
                id_empresas={empresas}
                defaultValue={selectedEmpresaId}
                enableOptionAll={true}
                onChangeValue={(value) => {
                  setIsSearched(false);
                  setValue('id_empresas', value, { shouldDirty: true });
                  setValue('id_pessoa', null, { shouldDirty: true })
                  if (value !== 'all') {
                    setSelectedEmpresaId(value);
                    setEmpresaIdSession(value);
                  } else {
                    setSelectedEmpresaId(null);
                    // setEmpresaIdSession(null);
                  }
                }}
                textHelper={false}
              />
            </Grid>

            {pessoas &&
                <Grid item xs={12} md={4}>
                  <TooltipSelectDisabled isDisabled={selectedEmpresaId}>
                    <PaginatedUserAutocomplete
                        name="id_pessoa"
                        control={control}
                        label="Colaborador"
                        disabled={!selectedEmpresaId}
                        options={pessoas}
                        getOptionLabel={(option) => {
                          // verify if the user exists in pessoas
                          const pessoa = pessoas.find(p => p.id === option);
                          return pessoa ? `${pessoa.matricula} - ${pessoa.nome}` : '';
                        }}
                        renderOption={(props, option) => (
                            <MenuItem {...props} key={option.id} disabled={option.status === 0}>
                              {option.matricula} - {option.nome} {option.status === 0 ? '- (Inativo)' : ''}
                            </MenuItem>
                        )}
                        onChange={(event, value) => {
                          setIsSearched(false);
                          setValue('id_pessoa', value ? value.id : null, { shouldDirty: true });
                          if (value) {
                            clearErrors('id_pessoa')
                          } else {
                            listaColaboradores(0, true);
                          }
                        }}
                        onInput={(event) => {
                          setPessoas([]);
                          setPagePessoas(0);
                          setHasMorePessoas(true);
                          const filterValue = event.target.value ?? "";
                          listaColaboradores(0, true, filterValue);
                        }}
                        hasMore={hasMorePessoas}
                        loadOptionsFn={() => listaColaboradores(pagePessoas, false, "")}
                        helperText={getFormErrorMessage(errors, 'id_pessoa')}
                        error={!!errors.id_pessoa}
                        loading={isLoadingPessoas}
                        textFieldSize="small"
                        textFieldStyle={{ backgroundColor: '#fff' }}
                    />
                  </TooltipSelectDisabled>
                </Grid>
            }

            {pessoas &&
              <Grid item xs={12} md={4}>
                <TooltipSelectDisabled isDisabled={selectedEmpresaId}>
                  <PaginatedUserAutocomplete
                    name="id_responsavel"
                    control={control}
                    label="Responsável Entrega"
                    disabled={!selectedEmpresaId}
                    options={pessoas}
                    getOptionLabel={(option) => {
                      // verify if the user exists in pessoas
                      const pessoa = pessoas.find(p => p.id === option);
                      return pessoa ? `${pessoa.matricula} - ${pessoa.nome}` : '';
                    }}
                    renderOption={(props, option) => (
                      <MenuItem {...props} key={option.id} disabled={option.status === 0}>
                        {option.matricula} - {option.nome} {option.status === 0 ? '- (Inativo)' : ''}
                      </MenuItem>
                    )}
                    onChange={(event, value) => {
                      setIsSearched(false);
                      setValue('id_responsavel', value ? value.id : null, { shouldDirty: true });
                      if (value) {
                        clearErrors('id_responsavel')
                      } else {
                        listaColaboradores(0, true);
                      }
                    }}
                    onInput={(event) => {
                      setPessoas([]);
                      setPagePessoas(0);
                      setHasMorePessoas(true);
                      const filterValue = event.target.value ?? "";
                      listaColaboradores(0, true, filterValue);
                    }}
                    hasMore={hasMorePessoas}
                    loadOptionsFn={() => listaColaboradores(pagePessoas, false, "")}
                    helperText={getFormErrorMessage(errors, 'id_responsavel')}
                    error={!!errors.id_responsavel}
                    loading={isLoadingPessoas}
                    textFieldSize="small"
                    textFieldStyle={{ backgroundColor: '#fff' }}
                  />
                </TooltipSelectDisabled>
              </Grid>
            }

            {pessoas &&
                <Grid item xs={12} md={4}>
                  <TooltipSelectDisabled isDisabled={selectedEmpresaId}>
                    <PaginatedUserAutocomplete
                        name="id_responsavel_aprovacao"
                        control={control}
                        label="Responsável Aprovação"
                        disabled={!selectedEmpresaId}
                        options={pessoas}
                        getOptionLabel={(option) => {
                          // verify if the user exists in pessoas
                          const pessoa = pessoas.find(p => p.id === option);
                          return pessoa ? `${pessoa.matricula} - ${pessoa.nome}` : '';
                        }}
                        renderOption={(props, option) => (
                            <MenuItem {...props} key={option.id} disabled={option.status === 0}>
                              {option.matricula} - {option.nome} {option.status === 0 ? '- (Inativo)' : ''}
                            </MenuItem>
                        )}
                        onChange={(event, value) => {
                          setIsSearched(false);
                          setValue('id_responsavel_aprovacao', value ? value.id : null, { shouldDirty: true });
                          if (value) {
                            clearErrors('id_responsavel_aprovacao')
                          } else {
                            listaColaboradores(0, true);
                          }
                        }}
                        onInput={(event) => {
                          setPessoas([]);
                          setPagePessoas(0);
                          setHasMorePessoas(true);
                          const filterValue = event.target.value ?? "";
                          listaColaboradores(0, true, filterValue);
                        }}
                        hasMore={hasMorePessoas}
                        loadOptionsFn={() => listaColaboradores(pagePessoas, false, "")}
                        helperText={getFormErrorMessage(errors, 'id_responsavel_aprovacao')}
                        error={!!errors.id_responsavel_aprovacao}
                        loading={isLoadingPessoas}
                        textFieldSize="small"
                        textFieldStyle={{ backgroundColor: '#fff' }}
                    />
                  </TooltipSelectDisabled>
                </Grid>
            }

            <Grid item xs={12} md={4}>
              <Controller
                name="statusRetirada"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="Status"
                    size="small"
                    onChange={(event) => {
                      setIsSearched(false);
                      field.onChange(event);
                    }}
                    InputProps={{ style: { backgroundColor: '#fff' } }}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="aceito">Aceito</MenuItem>
                    <MenuItem value="negado">Negado</MenuItem>
                    <MenuItem value="pendente">Pendente</MenuItem>
                    <MenuItem value="retirado">Retirado</MenuItem>
                    <MenuItem value="inativado">Inativado</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
          </Grid>

          <Grid item xs={12} md={4}>
            <DateRangePicker
              value={dateRange}
              bgColor='#fff'
              onChange={(value) => {
                setDateRange(value);
                const [start, end] = value;
                setValue('data_inicio', start ? dayjs(start).format('YYYY-MM-DD') : '');
                setValue('data_fim', end ? dayjs(end).format('YYYY-MM-DD') : '');
                setIsSearched(false);
              }}
              onReset={() => {
                setIsSearched(false);
                setDateRange([null, null]);
                setValue('data_inicio', '');
                setValue('data_fim', '');
              }}
              hasError={errors.data_inicio || errors.data_fim}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              type='submit'
              color='primary'
              variant='contained'
              disabled={isLoading || Object.keys(errors).length > 0 || isSearched}
              startIcon={isLoading ? <CircularProgress size={16} sx={{ color: "textSecondary" }} /> : <FilterAlt />}
            >
              Filtrar
            </Button>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table aria-label="Retiradas Extra">
              <TableHead>
                <TableRow sx={{ display: 'flex' }}>
                  <TableCell sx={{ flex: 2 }}>Funcionário</TableCell>
                  <TableCell sx={{ flex: 2, minWidth: 160 }}>Produto</TableCell>
                  <TableCell sx={{ flex: 3 }}>Justificativa</TableCell>
                  <TableCell sx={{ flex: 1, minWidth: 115 }}>Status</TableCell>
                  <TableCell sx={{ flex: 2 }}>Responsável Entrega</TableCell>
                  <TableCell sx={{ flex: 2 }}>Responsável Aprovação</TableCell>
                  <TableCell sx={{ flex: 2 }}>Data Retirada</TableCell>
                  <TableCell sx={{ flex: 2 }}>Data Requisição</TableCell>
                  <TableCell sx={{ flex: 2 }}>Quantidade</TableCell>
                  <TableCell sx={{ flex: 1 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? <LoadingTableRow />
                  : retiradasExtra && retiradasExtra?.length > 0
                    ? retiradasExtra.map((item) =>
                      <RetiradaExtraRow
                        key={item.id}
                        data={item}
                        handleOpenEditForm={(item) => {
                          setSelectedItem(item);
                          setOpenForm(true);
                        }}
                      />
                    )
                    : <EmptyTableRow />
                }
              </TableBody>
            </Table>

            <CustomTablePagination
              numTotalItems={numTotalItems}
              rowsPerPage={rowsPerPage}
              page={page}
              setPage={setPage}
              setRowsPerPage={setRowsPerPage}
            />
          </TableContainer>
        </Grid>
      </Grid>

      <ApproveRetiradaExtraModal
        infoRetirada={selectedItem}
        open={openForm}
        setOpen={(event) => {
          setOpenForm(event)
          if (!event) setSelectedItem(null);
        }}
      />
    </>
  );
}